/**
 * The panel that opens when a player taps a billboard.
 *
 * It shows what the sign says and offers one action: keep it. There is no
 * "visit website" button, because the client is never given a URL to visit.
 * Kept offers are read afterwards on the offers page, outside the game.
 */

import type { ServedAd } from '../api/gameTypes.js';

export interface SponsorPanelHandlers {
  /** Records that the panel was opened. Fire-and-forget. */
  onOpen: (ad: ServedAd) => void;
  /** Keeps the offer. Resolves false when it could not be kept. */
  onKeep: (ad: ServedAd) => Promise<boolean>;
}

export class SponsorPanel {
  private readonly root: HTMLDivElement;
  private readonly card: HTMLDivElement;
  private readonly handlers: SponsorPanelHandlers;
  private current: ServedAd | null = null;
  /** Slots kept this session, so the button reads correctly on a second look. */
  private readonly kept = new Set<string>();
  private lastFocused: HTMLElement | null = null;

  constructor(parent: HTMLElement, handlers: SponsorPanelHandlers) {
    this.handlers = handlers;

    this.root = document.createElement('div');
    this.root.className = 'sponsor-backdrop';
    this.root.hidden = true;

    this.card = document.createElement('div');
    this.card.className = 'sponsor-card';
    this.card.setAttribute('role', 'dialog');
    this.card.setAttribute('aria-modal', 'true');
    this.card.setAttribute('aria-labelledby', 'sponsor-title');
    this.root.append(this.card);
    parent.append(this.root);

    // Clicking the darkened area behind the card closes it, as it does
    // everywhere else. Clicks inside must not travel up and close it.
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });
    this.card.addEventListener('click', (event) => event.stopPropagation());

    document.addEventListener('keydown', (event) => {
      if (!this.root.hidden && event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(ad: ServedAd): void {
    this.current = ad;
    this.lastFocused = document.activeElement as HTMLElement | null;
    this.card.replaceChildren(...this.build(ad));
    this.root.hidden = false;
    this.handlers.onOpen(ad);

    // Focus lands on the panel itself, not the Keep button — landing on an
    // action means a stray Enter keeps an offer nobody chose.
    this.card.tabIndex = -1;
    this.card.focus();
  }

  close(): void {
    this.root.hidden = true;
    this.current = null;
    this.card.replaceChildren();
    this.lastFocused?.focus();
    this.lastFocused = null;
  }

  private build(ad: ServedAd): HTMLElement[] {
    const nodes: HTMLElement[] = [];

    const label = document.createElement('p');
    label.className = 'sponsor-label';
    // Said plainly, and said first. A player should never have to work out
    // whether they are looking at an advertisement.
    label.textContent = 'Advertisement';
    nodes.push(label);

    const title = document.createElement('h2');
    title.id = 'sponsor-title';
    title.className = 'sponsor-title';
    title.textContent = ad.advertiserName;
    nodes.push(title);

    // The still is used rather than the video: the panel is something to read,
    // not somewhere to play an advertisement at somebody.
    const artUrl = ad.kind === 'video' && ad.stillUrl !== '' ? ad.stillUrl : ad.imageUrl;
    if (artUrl !== '') {
      const art = document.createElement('img');
      art.className = 'sponsor-art';
      art.src = artUrl;
      art.alt = `${ad.advertiserName} advertisement`;
      art.loading = 'lazy';
      nodes.push(art);
    }

    if (ad.offerText !== '') {
      const offer = document.createElement('p');
      offer.className = 'sponsor-offer';
      offer.textContent = ad.offerText;
      nodes.push(offer);
    }

    if (ad.promoCode !== '') {
      const code = document.createElement('p');
      code.className = 'sponsor-code';
      const codeLabel = document.createElement('span');
      codeLabel.textContent = 'Code ';
      const codeValue = document.createElement('strong');
      codeValue.textContent = ad.promoCode;
      code.append(codeLabel, codeValue);
      nodes.push(code);
    }

    const alreadyKept = this.kept.has(ad.slotId);

    const note = document.createElement('p');
    note.className = 'sponsor-note';
    // Sets the expectation before the button is pressed: nothing is about to
    // open, and the offer will still be there later.
    note.textContent = alreadyKept
      ? 'Saved to your offers. It will be there when you finish playing.'
      : 'Kept offers wait for you on your offers page — nothing opens while you play.';

    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'sponsor-keep';
    keep.textContent = alreadyKept ? 'Kept' : 'Keep this offer';
    keep.disabled = alreadyKept;
    keep.addEventListener('click', () => { void this.keep(ad, keep, note); });

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'sponsor-close';
    // Not "cancel". Nothing was started, so there is nothing to cancel.
    back.textContent = 'Back to the game';
    back.addEventListener('click', () => this.close());

    const actions = document.createElement('div');
    actions.className = 'sponsor-actions';
    actions.append(keep, back);

    nodes.push(actions, note);
    return nodes;
  }

  private async keep(ad: ServedAd, button: HTMLButtonElement, note: HTMLElement): Promise<void> {
    button.disabled = true;
    button.textContent = 'Keeping…';
    const saved = await this.handlers.onKeep(ad);

    if (!saved) {
      // Says what happened and what to do, rather than apologising.
      button.disabled = false;
      button.textContent = 'Keep this offer';
      note.textContent = 'That offer could not be kept just now. Try again in a moment.';
      return;
    }

    this.kept.add(ad.slotId);
    button.textContent = 'Kept';
    note.textContent = 'Saved to your offers. It will be there when you finish playing.';
    if (this.current?.slotId === ad.slotId) {
      // Stays open for a beat so "Kept" is actually read, then gets out of
      // the way — a panel over the world is a panel over the game.
      window.setTimeout(() => {
        if (this.current?.slotId === ad.slotId) this.close();
      }, 1200);
    }
  }
}
